import { useState, useEffect } from 'react';
import { useAppStore } from '../store';
import { ERROR_CATEGORY_LABELS, ERROR_CATEGORY_COLORS } from '../types';
import { CheckCircle2, XCircle, ArrowRight, RotateCcw, Trophy, BookOpen, Lightbulb } from 'lucide-react';

export default function PracticePage() {
  const { practiceItems, currentPracticeIndex, submitAnswer, nextPractice, setActiveTab, favorites } = useAppStore();
  const [userInput, setUserInput] = useState('');
  const [showHint, setShowHint] = useState(false);

  const current = practiceItems[currentPracticeIndex];
  const totalItems = practiceItems.length;
  const completedCount = practiceItems.filter(p => p.isCorrect === true).length;
  const progress = totalItems > 0 ? (completedCount / totalItems) * 100 : 0;

  // Reset input when moving to next question
  useEffect(() => {
    setUserInput('');
    setShowHint(false);
  }, [currentPracticeIndex]);

  if (totalItems === 0) {
    const hasDueReviews = favorites.some((favorite) => {
      if (favorite.isMastered || !favorite.nextReviewAt) return false;
      const dueAt = Date.parse(favorite.nextReviewAt);
      return Number.isFinite(dueAt) && dueAt <= Date.now();
    });

    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="text-center">
          <BookOpen size={48} className="text-slate-300 mx-auto mb-4" />
          <h2 className="font-serif text-2xl text-primary mb-2">
            {favorites.length > 0 && !hasDueReviews ? '今天的复习已完成' : '暂无练习题'}
          </h2>
          <p className="text-slate-500 mb-6">
            {favorites.length > 0 && !hasDueReviews ? '今天没有到期错题，先去分析新作文，积累下一批复习材料。' : '前往分析结果页，选择要练习的错误。'}
          </p>
          <button onClick={() => setActiveTab('analysis')} className="px-6 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-slate-800 transition-colors">
            去分析页
          </button>
        </div>
      </div>
    );
  }

  // All completed
  if (completedCount === totalItems) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <Trophy size={40} className="text-emerald-600" />
          </div>
          <h2 className="font-serif text-3xl text-primary mb-3">全部订正完成</h2>
          <p className="text-slate-500 mb-2">
            你已经成功改对 {totalItems} 个句子。
          </p>
          <p className="text-sm text-slate-400 mb-8">
            已自动加入你的错题集，方便后续复习。
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <button onClick={() => setActiveTab('collection')} className="px-6 py-3 bg-accent text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors">
              查看错题集
            </button>
            <button onClick={() => setActiveTab('analysis')} className="px-6 py-3 border-2 border-slate-200 text-primary rounded-xl font-semibold hover:border-accent hover:text-accent transition-colors">
              返回分析结果
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!current) return null;

  const reviewStatusText = current.favorite
    ? current.favorite.isMastered
      ? '这条错误已经掌握'
      : `第 ${Math.max(1, current.favorite.reviewStage + 1)} 轮复习`
    : '新错题，答对后会进入复习计划';
  const siblingErrorCount = Math.max(0, current.sourceErrors.length - 1);

  const handleSubmit = () => {
    if (!userInput.trim()) return;
    void submitAnswer(userInput);
  };

  const handleNext = () => {
    // Find next unanswered item
    const nextUnanswered = practiceItems.findIndex((p, i) => i > currentPracticeIndex && p.isCorrect !== true);
    if (nextUnanswered !== -1) {
      // Move to next unanswered
      for (let i = currentPracticeIndex; i < nextUnanswered; i++) {
        nextPractice();
      }
    } else {
      // Move forward anyway
      nextPractice();
    }
  };

  return (
    <div className="min-h-screen bg-surface py-10 px-6">
      <div className="max-w-3xl mx-auto">
        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-3">
            <h1 className="font-serif text-2xl text-primary">改错练习</h1>
            <span className="text-sm font-semibold text-slate-500">
              {completedCount} / {totalItems} 已完成
            </span>
          </div>
          <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-accent rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8 mb-6">
          {/* Category badge */}
          <div className="flex flex-wrap gap-2 mb-6">
            {current.categories.map((category) => (
              <span
                key={category}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold text-white"
                style={{ backgroundColor: ERROR_CATEGORY_COLORS[category] }}
              >
                {ERROR_CATEGORY_LABELS[category]}
              </span>
            ))}
            <span className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold bg-slate-100 text-slate-600">
              {reviewStatusText}
            </span>
            {siblingErrorCount > 0 && (
              <span className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold bg-amber-50 text-amber-700">
                同句还有 {siblingErrorCount} 个其他错误，本轮先只改这一类
              </span>
            )}
          </div>

          {/* Error sentence */}
          <div className="mb-6">
            <div className="text-xs font-bold text-red-400 mb-2 tracking-wider">待修改句子</div>
            <div className="p-5 bg-red-50 rounded-xl border border-red-100">
              <p className="text-lg text-red-800 leading-relaxed font-medium">{current.error.originalSentence}</p>
            </div>
          </div>

          {/* Hint toggle */}
          {!current.isCorrect && (
            <button
              onClick={() => setShowHint(!showHint)}
              className="flex items-center gap-2 text-sm text-amber-600 hover:text-amber-700 mb-4 transition-colors"
            >
              <Lightbulb size={14} />
              {showHint ? '隐藏提示' : '查看提示'}
            </button>
          )}
          {showHint && !current.isCorrect && (
            <div className="p-4 bg-amber-50 rounded-xl border border-amber-100 mb-6">
              <p className="text-sm text-amber-800">{current.error.explanationZh || '暂无中文提示'}</p>
            </div>
          )}

          {/* User input */}
          {current.isCorrect === null || current.isCorrect === false ? (
            <div className="mb-6">
              <div className="text-xs font-bold text-slate-400 mb-2 tracking-wider">你的修改版本</div>
              <textarea
                value={userInput}
                onChange={e => setUserInput(e.target.value)}
                placeholder={siblingErrorCount > 0 ? '输入你修改后的句子，先把当前这个错误改对即可...' : '在这里输入修改后的句子...'}
                rows={3}
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all text-base leading-relaxed resize-none"
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
              />
            </div>
          ) : null}

          {/* Feedback */}
          {current.isCorrect === true && (
            <div className="p-5 bg-emerald-50 rounded-xl border border-emerald-200 mb-6 flex items-start gap-3">
              <CheckCircle2 size={24} className="text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-emerald-800 mb-1">回答正确</div>
                <p className="text-sm text-emerald-700">{current.error.correctedSentence}</p>
                <p className="text-xs text-emerald-500 mt-2">
                  {current.favorite ? '复习进度已更新。' : '已加入错题集，并安排下次复习。'}
                </p>
              </div>
            </div>
          )}

          {current.isCorrect === false && (
            <div className="p-5 bg-red-50 rounded-xl border border-red-200 mb-6 flex items-start gap-3">
              <XCircle size={24} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-red-800 mb-1">还不太对，再试一次</div>
                <p className="text-sm text-red-600">
                  第 {current.attempts} 次尝试。需要的话可以先看提示。
                </p>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3">
            {current.isCorrect !== true ? (
              <button
                onClick={handleSubmit}
                disabled={!userInput.trim()}
                className="group inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50"
              >
                检查答案
                <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="group inline-flex items-center gap-2 px-6 py-3 bg-accent text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors"
              >
                下一题
                <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
              </button>
            )}

            {current.isCorrect === false && current.attempts >= 3 && (
              <button
                onClick={() => {
                  setUserInput(current.acceptableAnswers[0] || current.error.correctedSentence);
                }}
                className="inline-flex items-center gap-2 px-6 py-3 border border-slate-200 text-slate-600 rounded-xl font-medium hover:bg-slate-50 transition-colors"
              >
                <RotateCcw size={14} />
                显示答案
              </button>
            )}
          </div>
        </div>

        {/* Progress indicators */}
        <div className="flex gap-1.5 justify-center flex-wrap">
          {practiceItems.map((item, i) => (
            <div
              key={i}
              className={`w-3 h-3 rounded-full transition-all ${
                item.isCorrect === true
                  ? 'bg-emerald-500'
                  : item.isCorrect === false
                  ? 'bg-red-400'
                  : i === currentPracticeIndex
                  ? 'bg-primary scale-125'
                  : 'bg-slate-200'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
